<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  NCard,
  NInputNumber,
  NForm,
  NFormItem,
  NInput,
  NButton,
  NSelect,
  NSpace,
  NTag,
  NDynamicInput,
  NResult,
  NPageHeader,
  NRadioGroup,
  NRadio,
  NText,
} from "naive-ui";
import { useMessage } from 'naive-ui'

// API utilities
import {
  API_BASE_URL,
  saveProvider as saveProviderService,
  fetchProviderProfile,
  formatProviderDataForForm,
  formatFormDataForApi,
  generateAboutMe
} from "@/services/provider";
import { authToken, isAuthenticated, authFetch } from "@/services/auth";
import specializations from '../data/specializations.json';
import providerTypes from '../data/providerTypes.json';
import procedures from '../data/procedures.json';
import states from '../data/states.json';
import languages from '../data/languages.json';
import paymentMethods from '../data/paymentMethods.json';
import accessibilityOptions from '../data/accessibilityOptions.json';

const message = useMessage()

const route = useRoute();
const router = useRouter();

// Determine if we're editing (id in URL) or creating new
const isEditMode = computed(() => !!route.params.id);
const pageTitle = computed(() => isEditMode.value ? "Edit Provider" : "Add New Provider");

// Form data with all new fields
const formData = ref({
  // Basic Information
  provider_name: "",
  provider_type: null,
  specializations: [],
  tags: [],
  additional_info: "",
  is_draft: false,

  // Address Details
  address_line1: "",
  address_line2: "",
  address_line3: "",
  city: "",
  state: "",
  postal_code: 0,
  additional_locations: [],

  // Contact Information
  phone_numbers: [],
  website_url: "",
  social_media: [],

  // Professional Details
  procedures: [],
  insurance_accepted: [],
  spoken_languages: [],
  education: [],
  certifications: [],
  years_of_experience: null,

  // Practice Information
  accepts_new_patients: null,
  telehealth_available: null,
  office_hours: [],
  parking_info: "",
  payment_methods: [],
  accessibility_options: [],
  average_wait_time_minutes: null,
});

const loading = ref(false);
const saving = ref(false);
const success = ref(false);
const error = ref(null);

// Validation errors
const validationErrors = ref({});

// Expanded steps to include new sections
const currentStep = ref(1);
const totalSteps = 7;

const stepTitles = [
  "Basic Information",
  "Address Details",
  "Contact Info",
  "Professional Details",
  "Practice Information",
  "About Me",
  "Review & Submit"
];

const currentStepTitle = computed(() => {
  return stepTitles[currentStep.value - 1] || "Unknown Step";
});

const specializationOptions = specializations.categories.map(cat => ({
  type: 'group',
  label: cat.name,
  key: cat.name,
  children: cat.specializations.map(s => ({
    label: s.label,
    value: s.value
  }))
}));

const providerTypeOptions = providerTypes.categories.map(cat => ({
  type: 'group',
  label: cat.name,
  key: cat.name,
  children: cat.types.map(t => ({
    label: t.label,
    value: t.value
  }))
}));

const stateOptions = states.states.map(s => ({ label: s.label, value: s.value }));

const languageOptions = languages.languages.map(l => ({ 
  label: l.native !== l.label ? `${l.label} (${l.native})` : l.label, 
  value: l.value 
}));

const paymentMethodOptions = paymentMethods.methods.map(m => ({ label: m.label, value: m.value }));

const accessibilityOpts = accessibilityOptions.options.map(o => ({ label: o.label, value: o.value }));

// Validation functions
// Helper function to clean up dynamic input arrays
const cleanupDynamicInputs = (data) => {
  const cleaned = { ...data };
  
  // Clean education - only include entries where ALL fields are filled
  if (cleaned.education && Array.isArray(cleaned.education)) {
    cleaned.education = cleaned.education.filter(edu => 
      edu && 
      edu.institution?.trim() && 
      edu.degree?.trim() && 
      edu.year
    );
  }
  
  // Clean certifications - only include entries where ALL fields are filled
  if (cleaned.certifications && Array.isArray(cleaned.certifications)) {
    cleaned.certifications = cleaned.certifications.filter(cert => 
      cert && 
      cert.name?.trim() && 
      cert.issuer?.trim() && 
      cert.year
    );
  }
  
  // Clean procedures - only include non-empty entries
  if (cleaned.procedures && Array.isArray(cleaned.procedures)) {
    cleaned.procedures = cleaned.procedures.filter(proc => 
      proc && proc.trim() && proc.trim().length >= 3
    );
  }
  
  // Clean tags - only include non-empty entries
  if (cleaned.tags && Array.isArray(cleaned.tags)) {
    cleaned.tags = cleaned.tags.filter(tag => 
      tag && tag.trim() && tag.trim().length >= 3
    );
  }
  
  // Clean phone numbers - only include non-empty entries
  if (cleaned.phone_numbers && Array.isArray(cleaned.phone_numbers)) {
    cleaned.phone_numbers = cleaned.phone_numbers.filter(phone => 
      phone && phone.trim()
    );
  }
  
  // Clean social media - only include entries with both platform and URL
  if (cleaned.social_media && Array.isArray(cleaned.social_media)) {
    cleaned.social_media = cleaned.social_media.filter(social => 
      social && social.platform && social.url?.trim()
    );
  }
  
  // Clean additional locations - only include complete entries
  if (cleaned.additional_locations && Array.isArray(cleaned.additional_locations)) {
    cleaned.additional_locations = cleaned.additional_locations.filter(loc => 
      loc && loc.address?.trim() && loc.city?.trim() && loc.state && loc.postal_code
    );
  }
  
  // Clean insurance accepted - only include non-empty entries
  if (cleaned.insurance_accepted && Array.isArray(cleaned.insurance_accepted)) {
    cleaned.insurance_accepted = cleaned.insurance_accepted.filter(ins => 
      ins && ins.trim()
    );
  }
  
  // Clean office hours - only include entries with all required fields
  if (cleaned.office_hours && Array.isArray(cleaned.office_hours)) {
    cleaned.office_hours = cleaned.office_hours.filter(hours => 
      hours && hours.day && hours.open && hours.close
    );
  }
  
  return cleaned;
};




const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

const validateURL = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const validateIndianPhone = (phone) => {
  // Indian phone number validation: 10 digits, can start with 6-9
  const re = /^[6-9]\d{9}$/;
  return re.test(phone.replace(/[\s\-\(\)\+]/g, ''));
};

const validatePincode = (pincode) => {
  // Indian pincode: 6 digits
  const re = /^\d{6}$/;
  return re.test(pincode.toString());
};

const validateYear = (year) => {
  const currentYear = new Date().getFullYear();
  const numYear = parseInt(year);
  return !isNaN(numYear) && numYear >= 1900 && numYear <= currentYear + 5;
};

const validateTime = (time) => {
  // Validate HH:MM format
  const re = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return re.test(time);
};

const validateStep1 = () => {
  const errors = {};

  // Provider name is required
  if (!formData.value.provider_name?.trim()) {
    errors.provider_name = "Provider name is required";
  } else if (formData.value.provider_name.length < 2) {
    errors.provider_name = "Provider name must be at least 2 characters";
  } else if (formData.value.provider_name.length > 200) {
    errors.provider_name = "Provider name cannot exceed 200 characters";
  }

  // Provider type is required
  if (!formData.value.provider_type) {
    errors.provider_type = "Provider type is required";
  }

  // Specializations are required (at least one)
  if (!formData.value.specializations || formData.value.specializations.length === 0) {
    errors.specializations = "At least one specialization is required";
  }

  // Validate additional info length
  if (formData.value.additional_info && formData.value.additional_info.length > 2000) {
    errors.additional_info = "Additional information cannot exceed 2000 characters";
  }

  // Validate procedures - each must be 3-100 characters
  if (formData.value.procedures) {
    formData.value.procedures.forEach((proc, index) => {
      if (proc && proc.trim()) {
        if (proc.trim().length < 3) {
          errors[`procedures_${index}`] = "Procedure must be at least 3 characters";
        } else if (proc.length > 50) {
          errors[`procedures_${index}`] = "Procedure cannot exceed 50 characters";
        }
      }
    });
    
    // Check for general procedures error
    const hasProcedureErrors = Object.keys(errors).some(key => key.startsWith('procedures_'));
    if (hasProcedureErrors) {
      errors.procedures = "Some procedures have validation errors. Each must be 3-50 characters.";
    }
  }

  // Validate tags - each must be 3-50 characters
  if (formData.value.tags) {
    formData.value.tags.forEach((tag, index) => {
      if (tag && tag.trim()) {
        if (tag.trim().length < 3) {
          errors[`tags_${index}`] = "Tag must be at least 3 characters";
        } else if (tag.length > 25) {
          errors[`tags_${index}`] = "Tag cannot exceed 25 characters";
        }
      }
    });
    
    // Check for general tags error
    const hasTagErrors = Object.keys(errors).some(key => key.startsWith('tags_'));
    if (hasTagErrors) {
      errors.tags = "Some tags have validation errors. Each must be 3-25 characters.";
    }
  }

  return errors;
};

const validateStep2 = () => {
  const errors = {};

  // Address line 1 is required
  if (!formData.value.address_line1?.trim()) {
    errors.address_line1 = "Address line 1 is required";
  } else if (formData.value.address_line1.length > 200) {
    errors.address_line1 = "Address line 1 cannot exceed 200 characters";
  }

  // City is required
  if (!formData.value.city?.trim()) {
    errors.city = "City is required";
  } else if (formData.value.city.length > 100) {
    errors.city = "City name cannot exceed 100 characters";
  }

  // State is required
  if (!formData.value.state?.trim()) {
    errors.state = "State is required";
  }

  // Postal code is required and must be valid
  if (!formData.value.postal_code) {
    errors.postal_code = "Postal code is required";
  } else if (!validatePincode(formData.value.postal_code)) {
    errors.postal_code = "Please enter a valid 6-digit postal code";
  }

  // Validate address line 2 length if provided
  if (formData.value.address_line2 && formData.value.address_line2.length > 200) {
    errors.address_line2 = "Address line 2 cannot exceed 200 characters";
  }

  // Validate address line 3 length if provided
  if (formData.value.address_line3 && formData.value.address_line3.length > 200) {
    errors.address_line3 = "Address line 3 cannot exceed 200 characters";
  }

  // Validate additional locations
  if (formData.value.additional_locations) {
    formData.value.additional_locations.forEach((location, index) => {
      if (location.postal_code && !validatePincode(location.postal_code)) {
        errors[`additional_locations_${index}`] = `Invalid postal code in additional location ${index + 1}`;
      }
    });
  }

  return errors;
};

const validateStep3 = () => {
  const errors = {};

  // At least one phone number is required
  const validPhones = formData.value.phone_numbers?.filter(phone => phone?.trim()) || [];
  if (validPhones.length === 0) {
    errors.phone_numbers = "At least one phone number is required";
  } else {
    // Validate phone number format
    const invalidPhones = validPhones.filter(phone => !validateIndianPhone(phone));
    if (invalidPhones.length > 0) {
      errors.phone_numbers = "Please enter valid Indian phone numbers (10 digits starting with 6-9)";
    }
  }

  // Validate website URL
  if (formData.value.website_url && formData.value.website_url.trim()) {
    if (!validateURL(formData.value.website_url)) {
      errors.website_url = "Please enter a valid website URL (e.g., https://www.example.com)";
    }
  }

  // Validate social media URLs
  if (formData.value.social_media) {
    const invalidSocial = formData.value.social_media.filter(social =>
      social?.url?.trim() && !validateURL(social.url) && !social.url.startsWith('@')
    );
    if (invalidSocial.length > 0) {
      errors.social_media = "Please enter valid URLs or usernames (starting with @) for social media";
    }
  }

  return errors;
};

const validateStep4 = () => {
  const errors = {};

  // Spoken languages are required (at least one)
  if (!formData.value.spoken_languages || formData.value.spoken_languages.length === 0) {
    errors.spoken_languages = "At least one spoken language is required";
  }

  // Years of experience is required
  if (formData.value.years_of_experience === null || formData.value.years_of_experience === undefined || formData.value.years_of_experience === "") {
    errors.years_of_experience = "Years of experience is required";
  } else if (formData.value.years_of_experience < 0 || formData.value.years_of_experience > 80) {
    errors.years_of_experience = "Years of experience must be between 0 and 80";
  }

  // Validate education entries - ALL fields required if entry exists
  if (formData.value.education) {
    let hasEducationError = false;
    formData.value.education.forEach((edu, index) => {
      // If ANY field is filled, ALL fields are required
      const hasAnyField = edu.institution || edu.degree || edu.year;
      
      if (hasAnyField) {
        if (!edu.institution || !edu.institution.trim()) {
          errors[`education_${index}_institution`] = "Institution is required";
          hasEducationError = true;
        } else if (edu.institution.length > 200) {
          errors[`education_${index}_institution`] = "Institution name is too long (max 200 characters)";
          hasEducationError = true;
        }
        
        if (!edu.degree || !edu.degree.trim()) {
          errors[`education_${index}_degree`] = "Degree/Qualification is required";
          hasEducationError = true;
        } else if (edu.degree.length > 200) {
          errors[`education_${index}_degree`] = "Degree name is too long (max 200 characters)";
          hasEducationError = true;
        }
        
        if (!edu.year) {
          errors[`education_${index}_year`] = "Year is required";
          hasEducationError = true;
        } else if (!validateYear(edu.year)) {
          errors[`education_${index}_year`] = `Year must be between 1900 and ${new Date().getFullYear() + 5}`;
          hasEducationError = true;
        }
      }
    });
    
    if (hasEducationError) {
      errors.education = "Some education entries are incomplete or invalid. Please fill all fields.";
    }
  }

  // Validate certification entries - ALL fields required if entry exists
  if (formData.value.certifications) {
    let hasCertificationError = false;
    formData.value.certifications.forEach((cert, index) => {
      // If ANY field is filled, ALL fields are required
      const hasAnyField = cert.name || cert.issuer || cert.year;
      
      if (hasAnyField) {
        if (!cert.name || !cert.name.trim()) {
          errors[`certification_${index}_name`] = "Certification name is required";
          hasCertificationError = true;
        } else if (cert.name.length > 200) {
          errors[`certification_${index}_name`] = "Certification name is too long (max 200 characters)";
          hasCertificationError = true;
        }
        
        if (!cert.issuer || !cert.issuer.trim()) {
          errors[`certification_${index}_issuer`] = "Issuer is required";
          hasCertificationError = true;
        } else if (cert.issuer.length > 200) {
          errors[`certification_${index}_issuer`] = "Issuer name is too long (max 200 characters)";
          hasCertificationError = true;
        }
        
        if (!cert.year) {
          errors[`certification_${index}_year`] = "Year is required";
          hasCertificationError = true;
        } else if (!validateYear(cert.year)) {
          errors[`certification_${index}_year`] = `Year must be between 1900 and ${new Date().getFullYear() + 5}`;
          hasCertificationError = true;
        }
      }
    });
    
    if (hasCertificationError) {
      errors.certifications = "Some certification entries are incomplete or invalid. Please fill all fields.";
    }
  }

  return errors;
};

const validateStep5 = () => {
  const errors = {};

  // Validate office hours
  if (formData.value.office_hours) {
    formData.value.office_hours.forEach((hours, index) => {
      if (hours.open && !validateTime(hours.open)) {
        errors[`office_hours_${index}_open`] = `Invalid opening time format in office hours entry ${index + 1}. Use HH:MM format`;
      }
      if (hours.close && !validateTime(hours.close)) {
        errors[`office_hours_${index}_close`] = `Invalid closing time format in office hours entry ${index + 1}. Use HH:MM format`;
      }
      if (hours.open && hours.close && validateTime(hours.open) && validateTime(hours.close)) {
        const openTime = new Date(`2000-01-01T${hours.open}:00`);
        const closeTime = new Date(`2000-01-01T${hours.close}:00`);
        if (openTime >= closeTime) {
          errors[`office_hours_${index}_time`] = `Opening time must be before closing time in office hours entry ${index + 1}`;
        }
      }
    });
  }

  // Validate average wait time
  if (formData.value.average_wait_time_minutes !== null && formData.value.average_wait_time_minutes !== undefined) {
    if (formData.value.average_wait_time_minutes < 0 || formData.value.average_wait_time_minutes > 480) {
      errors.average_wait_time_minutes = "Average wait time must be between 0 and 480 minutes (8 hours)";
    }
  }

  // Validate parking info length
  if (formData.value.parking_info && formData.value.parking_info.length > 500) {
    errors.parking_info = "Parking information cannot exceed 500 characters";
  }

  return errors;
};

const isGenerating = ref(false);
const showManualEntry = ref(false);

const aiOptions = ref({
  tone: 'professional',
  length: 'medium',
  language: 'english'
});

const canGenerate = computed(() => {
  return formData.value.provider_name && formData.value.provider_type;
});

async function handleGenerateAboutMe() {

  if (!isAuthenticated.value) {
    message.error('Authentication required');
    return;
  }

  if (!canGenerate.value) {
    message.warning('Please fill in at least Provider Name and Type to generate AI content');
    return;
  }

  const selectedLanguage = ref(localStorage.getItem('preferredLanguage') || 'english');
  const language = selectedLanguage.value;

  isGenerating.value = true;

  try {
    const result = await generateAboutMe(
      authToken.value,
      formData.value,
      aiOptions.value.tone,
      aiOptions.value.length,
      language
    );

    if (result.success) {
      formData.value.additional_info = result.about_me;
      showManualEntry.value = true; // Show the textarea
      message.success('AI content generated successfully! You can edit it below.');
    } else {
      message.error(result.error || 'Failed to generate content');
    }
  } catch (error) {
    message.error('An error occurred while generating content');
    console.error('Generate error:', error);
  } finally {
    isGenerating.value = false;
  }
}

const validateCurrentStep = () => {
  let errors = {};

  switch (currentStep.value) {
    case 1:
      errors = validateStep1();
      break;
    case 2:
      errors = validateStep2();
      break;
    case 3:
      errors = validateStep3();
      break;
    case 4:
      errors = validateStep4();
      break;
    case 5:
      errors = validateStep5();
      break;
  }

  validationErrors.value = errors;
  return Object.keys(errors).length === 0;
};

const showValidationError = (errorMessage) => {
  message.error(errorMessage);
};

// Fetch provider data if in edit mode
onMounted(async () => {
  if (isEditMode.value) {
    try {
      loading.value = true;
      if (!isAuthenticated.value) {
        throw new Error("Authentication required");
      }

      const selectedLanguage = ref(localStorage.getItem('preferredLanguage') || 'english');
      const language = selectedLanguage.value;

      const providerData = await fetchProviderProfile(
        authToken.value,
        language
      );

      const formattedData = formatProviderDataForForm(providerData);

      formData.value = formattedData;
    } catch (err) {
      error.value = "Failed to load provider data";
      console.error(err);
    } finally {
      loading.value = false;
    }
  }
});

// Move to next step with validation
const nextStep = () => {
  if (!validateCurrentStep()) {
    const errorCount = Object.keys(validationErrors.value).length;
    showValidationError(`Please fix ${errorCount} validation error${errorCount > 1 ? 's' : ''} before proceeding.`);
    return;
  }

  if (currentStep.value < totalSteps) {
    currentStep.value++;
    window.scrollTo(0, 0);
    validationErrors.value = {}; // Clear errors when moving to next step
  }
};

// Move to previous step
const prevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
    window.scrollTo(0, 0);
    validationErrors.value = {}; // Clear errors when moving to previous step
  }
};

// Save provider using the service
// Save provider using the service
const saveProvider = async () => {
  // Validate all steps before saving
  const allErrors = {
    ...validateStep1(),
    ...validateStep2(),
    ...validateStep3(),
    ...validateStep4(),
    ...validateStep5()
  };

  if (Object.keys(allErrors).length > 0) {
    validationErrors.value = allErrors;
    const errorCount = Object.keys(allErrors).length;
    showValidationError(`Please fix ${errorCount} validation error${errorCount > 1 ? 's' : ''} before saving.`);
    return;
  }

  saving.value = true;
  error.value = null;

  try {
    if (!isAuthenticated.value) {
      throw new Error("Authentication required");
    }

    // Clean up the form data before sending
    const cleanedData = cleanupDynamicInputs(formData.value);

    if (Array.isArray(cleanedData.office_hours)) {
      cleanedData.office_hours.forEach((hour, index) => {
        console.log(`Office hour ${index}:`, hour);
      });
    }

    const payload = { ...cleanedData };

    if (isEditMode.value) {
      payload.id = route.params.id;
    } else {
      console.log('Create mode - no ID set');
    }

    let transformedOfficeHours = {};
    if (Array.isArray(payload.office_hours)) {
      payload.office_hours.forEach((item, index) => {
        if (item && item.day) {
          const day = item.day;
          const open = item.open || '';
          const close = item.close || '';

          if (!transformedOfficeHours[day]) {
            transformedOfficeHours[day] = [];
          }

          const timeSlot = {
            open: open,
            close: close
          };

          transformedOfficeHours[day].push(timeSlot);
        } else {
          console.log(`Skipping invalid item ${index}:`, item);
        }
      });
    } else {
      console.log('office_hours is not an array, setting to empty object');
    }

    const savedProvider = await saveProviderService(authToken.value, payload);

    success.value = true;
    validationErrors.value = {}; // Clear all validation errors on success

    if (!isEditMode.value) {
      formData.value = {
        provider_name: "",
        provider_type: null,
        specializations: [],
        tags: [],
        additional_info: "",
        is_draft: false,
        address_line1: "",
        address_line2: "",
        address_line3: "",
        city: "",
        state: "",
        postal_code: 0,
        additional_locations: [],
        phone_numbers: [],
        website_url: "",
        social_media: [],
        procedures: [],
        insurance_accepted: [],
        spoken_languages: [],
        education: [],
        certifications: [],
        years_of_experience: null,
        accepts_new_patients: null,
        telehealth_available: null,
        office_hours: [],
        parking_info: "",
        payment_methods: [],
        accessibility_options: [],
        average_wait_time_minutes: null,
      };
    }

    setTimeout(() => {
      router.push('/provider-profile');
    }, 2000);

  } catch (err) {
    console.error('=== SAVE ERROR ===');
    console.error('Error object:', err);
    console.error('Error message:', err.message);
    console.error('Error stack:', err.stack);

    error.value = err.message || "Failed to save provider";
  } finally {
    saving.value = false;
  }
};

// Helper functions for dynamic inputs
const createPhoneNumber = () => "";
const createTag = () => "";
const createSocialMedia = () => ({ platform: "", url: "" });
const createEducation = () => ({ institution: "", degree: "", year: "" });
const createCertification = () => ({ name: "", year: "", issuer: "" });
const createLocation = () => ({ address: "", city: "", state: "", postal_code: "" });
const createOfficeHours = () => ({ day: "Monday", open: "09:00", close: "17:00" });

// Cancel and go back
const handleCancel = () => {
  router.back();
};

// Helper function to get validation error for a field
const getValidationError = (fieldName) => {
  return validationErrors.value[fieldName];
};
</script>

<template>
  <div class="provider-form-container">
    <NPageHeader :title="pageTitle" @back="handleCancel" />

    <!-- Loading state -->
    <div v-if="loading" class="loading-container">
      <p>Loading provider data...</p>
    </div>

    <!-- Success message -->
    <NResult v-else-if="success" status="success" :title="isEditMode ? 'Provider Updated!' : 'Provider Created!'"
      description="Your provider information has been saved successfully.">
      <template #footer>
        <NButton type="primary" @click="router.push('/my-providers')">
          Return to My Providers
        </NButton>
      </template>
    </NResult>

    <!-- Error message -->
    <NResult v-else-if="error" status="error" title="Something Went Wrong" :description="error">
      <template #footer>
        <NButton @click="error = null">
          Try Again
        </NButton>
      </template>
    </NResult>

    <!-- Form content -->
    <div v-else class="provider-form">
      <!-- Simplified Step Indicator -->
      <div class="step-indicator">
        <div class="step-progress">
          <div class="progress-bar" :style="{ width: `${(currentStep / totalSteps) * 100}%` }"></div>
        </div>
        <div class="step-info">
          <div class="step-title">
            <h3>{{ currentStepTitle }}</h3>
          </div>
          <div class="step-count">Step {{ currentStep }} of {{ totalSteps }}</div>
        </div>
      </div>

      <!-- Step 1: Basic Information -->
      <NCard v-if="currentStep === 1" title="Basic Information" class="form-card">
        <NForm :model="formData" label-placement="top">
          <NFormItem label="Provider Name" required
            :validation-status="getValidationError('provider_name') ? 'error' : undefined"
            :feedback="getValidationError('provider_name')">
            <NInput v-model:value="formData.provider_name" placeholder="Enter provider name" size="large"
              :status="getValidationError('provider_name') ? 'error' : undefined" />
          </NFormItem>

          <NFormItem label="Provider Type" required
            :validation-status="getValidationError('provider_type') ? 'error' : undefined"
            :feedback="getValidationError('provider_type')">
            <NSelect v-model:value="formData.provider_type" :options="providerTypeOptions"
              placeholder="Select provider type" filterable size="large"
              :status="getValidationError('provider_type') ? 'error' : undefined" />
          </NFormItem>

          <NFormItem label="Specializations" required
            :validation-status="getValidationError('specializations') ? 'error' : undefined"
            :feedback="getValidationError('specializations')">
            <NSelect v-model:value="formData.specializations" multiple :options="specializationOptions"
              placeholder="Select specializations" filterable tag size="large"
              :status="getValidationError('specializations') ? 'error' : undefined" />
          </NFormItem>

          <!-- Procedures with external help text and character limits -->
          <div class="form-field-with-help" style="margin-top: 24px;">
            <div class="help-text-external">
              Enter medical procedures and services offered by this provider. Each procedure must be 3-50 characters.
            </div>
            <NFormItem 
              label="Procedures Offered"
              :validation-status="getValidationError('procedures') ? 'error' : undefined"
              :feedback="getValidationError('procedures')">
              <NDynamicInput 
                v-model:value="formData.procedures" 
                :on-create="createTag" 
                placeholder="Add procedures"
                #="{ value, index }">
                <div style="width: 100%">
                  <NInput 
                    v-model:value="formData.procedures[index]" 
                    placeholder="Enter procedure (3-50 characters)"
                    maxlength="50"
                    show-count
                    :status="getValidationError(`procedures_${index}`) ? 'error' : undefined"
                    class="full-width-input" />
                  <div v-if="getValidationError(`procedures_${index}`)" class="error-text">
                    {{ getValidationError(`procedures_${index}`) }}
                  </div>
                </div>
              </NDynamicInput>
            </NFormItem>
          </div>

          <!-- Tags with character limits -->
          <NFormItem 
            label="Tags"
            :validation-status="getValidationError('tags') ? 'error' : undefined"
            :feedback="getValidationError('tags')">
            <NDynamicInput 
              v-model:value="formData.tags" 
              :on-create="createTag" 
              placeholder="Add tags"
              #="{ value, index }">
              <div style="width: 100%">
                <NInput 
                  v-model:value="formData.tags[index]" 
                  placeholder="Enter tag (3-25 characters)"
                  maxlength="25"
                  show-count
                  :status="getValidationError(`tags_${index}`) ? 'error' : undefined"
                  class="full-width-input" />
                <div v-if="getValidationError(`tags_${index}`)" class="error-text">
                  {{ getValidationError(`tags_${index}`) }}
                </div>
              </div>
            </NDynamicInput>
          </NFormItem>

        </NForm>

        <template #action>
          <NSpace justify="end" class="form-actions">
            <NButton @click="handleCancel" class="action-button">Cancel</NButton>
            <NButton type="primary" @click="nextStep" class="action-button">Next: Address Details</NButton>
          </NSpace>
        </template>
      </NCard>

      <!-- Step 2: Address Information -->
      <NCard v-if="currentStep === 2" title="Address Information" class="form-card">
        <NForm :model="formData" label-placement="top">
          <NFormItem label="Address Line 1" required
            :validation-status="getValidationError('address_line1') ? 'error' : undefined"
            :feedback="getValidationError('address_line1')">
            <NInput v-model:value="formData.address_line1" placeholder="Street address, building number" size="large"
              :status="getValidationError('address_line1') ? 'error' : undefined" />
          </NFormItem>

          <NFormItem label="Address Line 2"
            :validation-status="getValidationError('address_line2') ? 'error' : undefined"
            :feedback="getValidationError('address_line2')">
            <NInput v-model:value="formData.address_line2" placeholder="Apartment, suite, unit, floor (optional)"
              size="large" :status="getValidationError('address_line2') ? 'error' : undefined" />
          </NFormItem>

          <NFormItem label="Address Line 3"
            :validation-status="getValidationError('address_line3') ? 'error' : undefined"
            :feedback="getValidationError('address_line3')">
            <NInput v-model:value="formData.address_line3" placeholder="Additional address information (optional)"
              size="large" :status="getValidationError('address_line3') ? 'error' : undefined" />
          </NFormItem>

          <div class="form-row">
            <NFormItem label="City" class="form-col" required
              :validation-status="getValidationError('city') ? 'error' : undefined"
              :feedback="getValidationError('city')">
              <NInput v-model:value="formData.city" placeholder="Enter city" size="large"
                :status="getValidationError('city') ? 'error' : undefined" />
            </NFormItem>

            <NFormItem label="State" class="form-col" required
              :validation-status="getValidationError('state') ? 'error' : undefined"
              :feedback="getValidationError('state')">
              <NSelect v-model:value="formData.state" :options="stateOptions" placeholder="Select state" filterable
                size="large" :status="getValidationError('state') ? 'error' : undefined" />
            </NFormItem>

            <NFormItem label="Postal Code" class="form-col" required
              :validation-status="getValidationError('postal_code') ? 'error' : undefined"
              :feedback="getValidationError('postal_code')">
              <NInputNumber v-model:value="formData.postal_code" placeholder="110001" size="large" :show-button="false"
                :min="100000" :max="999999" class="w-full"
                :status="getValidationError('postal_code') ? 'error' : undefined" />
            </NFormItem>
          </div>

          <!-- Address Preview -->
          <div class="address-preview" v-if="formData.address_line1 || formData.city">
            <h4>📮 Address Preview:</h4>
            <div class="preview-address">
              <div v-if="formData.address_line1">
                {{ formData.address_line1 }}
              </div>
              <div v-if="formData.address_line2">
                {{ formData.address_line2 }}
              </div>
              <div v-if="formData.address_line3">
                {{ formData.address_line3 }}
              </div>
              <div v-if="formData.city || formData.state || formData.postal_code">
                {{ formData.city }}{{ formData.city && (formData.state || formData.postal_code) ? ", " : "" }}
                {{ formData.state }} {{ formData.postal_code }}
              </div>
            </div>
          </div>

          <!-- Additional Locations -->
          <NFormItem label="Additional Locations">
            <NDynamicInput v-model:value="formData.additional_locations" :on-create="createLocation"
              #="{ value, index }">
              <div class="additional-location">
                <NInput v-model:value="formData.additional_locations[index].address" placeholder="Address"
                  class="mb-2" />
                <div class="form-row mobile-form-row">
                  <NInput v-model:value="formData.additional_locations[index].city" placeholder="City" />
                  <NSelect v-model:value="formData.additional_locations[index].state" :options="stateOptions"
                    placeholder="State" filterable />
                  <NInputNumber v-model:value="formData.additional_locations[index].postal_code"
                    placeholder="Postal Code" :min="100000" :max="999999" :show-button="false"
                    :status="getValidationError(`additional_locations_${index}`) ? 'error' : undefined" />
                </div>
                <div v-if="getValidationError(`additional_locations_${index}`)" class="error-text">
                  {{ getValidationError(`additional_locations_${index}`) }}
                </div>
              </div>
            </NDynamicInput>
          </NFormItem>
        </NForm>

        <template #action>
          <NSpace justify="space-between" class="form-actions">
            <NButton @click="prevStep" class="action-button">Back: Basic Information</NButton>
            <NButton type="primary" @click="nextStep" class="action-button">Next: Contact Info</NButton>
          </NSpace>
        </template>
      </NCard>

      <!-- Step 3: Contact Information -->
      <NCard v-if="currentStep === 3" title="Contact Information" class="form-card">
        <NForm :model="formData" label-placement="top">
          <!-- Phone Numbers with external help text -->
          <div class="form-field-with-help">
            <div class="help-text-external">Enter valid Indian phone numbers (10 digits starting with 6-9)</div>
            <NFormItem label="Phone Numbers" required
              :validation-status="getValidationError('phone_numbers') ? 'error' : undefined"
              :feedback="getValidationError('phone_numbers')">
              <NDynamicInput v-model:value="formData.phone_numbers" :on-create="createPhoneNumber"
                placeholder="Add phone number" #="{ value, index }">
                <NInput v-model:value="formData.phone_numbers[index]" placeholder="91xxxxxxxx" size="large"
                  :status="getValidationError('phone_numbers') ? 'error' : undefined" class="full-width-input" />
              </NDynamicInput>
            </NFormItem>
          </div>

          <NFormItem label="Website URL" :validation-status="getValidationError('website_url') ? 'error' : undefined"
            :feedback="getValidationError('website_url')">
            <NInput v-model:value="formData.website_url" placeholder="https://www.example.com" size="large"
              :status="getValidationError('website_url') ? 'error' : undefined" />
          </NFormItem>

          <!-- Social Media with external help text -->
          <div class="form-field-with-help">
            <div class="help-text-external">Enter valid URLs (https://...) or usernames starting with @</div>
            <NFormItem label="Social Media"
              :validation-status="getValidationError('social_media') ? 'error' : undefined"
              :feedback="getValidationError('social_media')">
              <NDynamicInput v-model:value="formData.social_media" :on-create="createSocialMedia" #="{ value, index }">
                <div class="social-media-item">
                  <NSelect v-model:value="formData.social_media[index].platform" placeholder="Platform" :options="[
                    { label: 'Facebook', value: 'Facebook' },
                    { label: 'Twitter', value: 'Twitter' },
                    { label: 'Instagram', value: 'Instagram' },
                    { label: 'LinkedIn', value: 'LinkedIn' },
                    { label: 'YouTube', value: 'YouTube' },
                    { label: 'WhatsApp', value: 'WhatsApp' },
                  ]" class="platform-select" />
                  <NInput v-model:value="formData.social_media[index].url" placeholder="URL or @username"
                    class="url-input" />
                </div>
              </NDynamicInput>
            </NFormItem>
          </div>
        </NForm>

        <template #action>
          <NSpace justify="space-between" class="form-actions">
            <NButton @click="prevStep" class="action-button">Back: Address Details</NButton>
            <NButton type="primary" @click="nextStep" class="action-button">Next: Professional Details</NButton>
          </NSpace>
        </template>
      </NCard>

      <!-- Step 4: Professional Details -->
      <NCard v-if="currentStep === 4" title="Professional Details" class="form-card">
        <NForm :model="formData" label-placement="top">
          <!-- Insurance with external help text -->
          <div class="form-field-with-help">
            <div class="help-text-external">List insurance plans accepted by this provider.</div>
            <NFormItem label="Insurance Accepted">
              <NDynamicInput v-model:value="formData.insurance_accepted" :on-create="createTag"
                placeholder="Add insurance" />
            </NFormItem>
          </div>

          <NFormItem label="Spoken Languages" required
            :validation-status="getValidationError('spoken_languages') ? 'error' : undefined"
            :feedback="getValidationError('spoken_languages')">
            <NSelect v-model:value="formData.spoken_languages" multiple :options="languageOptions"
              placeholder="Select languages" filterable tag size="large"
              :status="getValidationError('spoken_languages') ? 'error' : undefined" />
          </NFormItem>

          <NFormItem label="Years of Experience" required
            :validation-status="getValidationError('years_of_experience') ? 'error' : undefined"
            :feedback="getValidationError('years_of_experience')">
            <NInputNumber v-model:value="formData.years_of_experience" placeholder="Years of professional experience"
              size="large" :show-button="false" :min="0" :max="80"
              :status="getValidationError('years_of_experience') ? 'error' : undefined" />
          </NFormItem>

          <!-- Education with improved validation -->
          <div class="form-field-with-help">
            <div class="help-text-external">
              All three fields (Institution, Degree, Year) are required if you add an education entry. Enter year as a 4-digit number (e.g., 2020).
            </div>
            <NFormItem 
              label="Education"
              :validation-status="getValidationError('education') ? 'error' : undefined"
              :feedback="getValidationError('education')">
              <NDynamicInput v-model:value="formData.education" :on-create="createEducation" #="{ value, index }">
                <div class="education-item">
                  <NInput 
                    v-model:value="formData.education[index].institution" 
                    placeholder="Institution (Required)"
                    class="mb-2 full-width-input"
                    :status="getValidationError(`education_${index}_institution`) ? 'error' : undefined" />
                  <div v-if="getValidationError(`education_${index}_institution`)" class="error-text">
                    {{ getValidationError(`education_${index}_institution`) }}
                  </div>
                  <div class="form-row mobile-form-row">
                    <NInput 
                      v-model:value="formData.education[index].degree" 
                      placeholder="Degree/Qualification (Required)"
                      :status="getValidationError(`education_${index}_degree`) ? 'error' : undefined"
                      class="flex-input" />
                    <NInputNumber 
                      v-model:value="formData.education[index].year" 
                      placeholder="Year (Required)"
                      :show-button="false" 
                      :min="1900" 
                      :max="2030"
                      :status="getValidationError(`education_${index}_year`) ? 'error' : undefined"
                      class="year-input" />
                  </div>
                  <div v-if="getValidationError(`education_${index}_degree`)" class="error-text">
                    {{ getValidationError(`education_${index}_degree`) }}
                  </div>
                  <div v-if="getValidationError(`education_${index}_year`)" class="error-text">
                    {{ getValidationError(`education_${index}_year`) }}
                  </div>
                </div>
              </NDynamicInput>
            </NFormItem>
          </div>

          <!-- Certifications with improved validation -->
          <div class="form-field-with-help">
            <div class="help-text-external">
              All three fields (Name, Issuer, Year) are required if you add a certification entry. Enter year as a 4-digit number (e.g., 2020).
            </div>
            <NFormItem 
              label="Certifications"
              :validation-status="getValidationError('certifications') ? 'error' : undefined"
              :feedback="getValidationError('certifications')">
              <NDynamicInput v-model:value="formData.certifications" :on-create="createCertification"
                #="{ value, index }">
                <div class="certification-item">
                  <NInput 
                    v-model:value="formData.certifications[index].name" 
                    placeholder="Certification Name (Required)"
                    class="mb-2 full-width-input"
                    :status="getValidationError(`certification_${index}_name`) ? 'error' : undefined" />
                  <div v-if="getValidationError(`certification_${index}_name`)" class="error-text">
                    {{ getValidationError(`certification_${index}_name`) }}
                  </div>
                  <div class="form-row mobile-form-row">
                    <NInput 
                      v-model:value="formData.certifications[index].issuer" 
                      placeholder="Issuing Organization (Required)"
                      :status="getValidationError(`certification_${index}_issuer`) ? 'error' : undefined"
                      class="flex-input" />
                    <NInputNumber 
                      v-model:value="formData.certifications[index].year" 
                      placeholder="Year (Required)"
                      :show-button="false" 
                      :min="1900" 
                      :max="2030"
                      :status="getValidationError(`certification_${index}_year`) ? 'error' : undefined"
                      class="year-input" />
                  </div>
                  <div v-if="getValidationError(`certification_${index}_issuer`)" class="error-text">
                    {{ getValidationError(`certification_${index}_issuer`) }}
                  </div>
                  <div v-if="getValidationError(`certification_${index}_year`)" class="error-text">
                    {{ getValidationError(`certification_${index}_year`) }}
                  </div>
                </div>
              </NDynamicInput>
            </NFormItem>
          </div>
        </NForm>

        <template #action>
          <NSpace justify="space-between" class="form-actions">
            <NButton @click="prevStep" class="action-button">Back: Contact Info</NButton>
            <NButton type="primary" @click="nextStep" class="action-button">Next: Practice Information</NButton>
          </NSpace>
        </template>
      </NCard>

      <!-- Step 5: Practice Information -->
      <NCard v-if="currentStep === 5" title="Practice Information" class="form-card">
        <NForm :model="formData" label-placement="top">
          <NFormItem label="Accepting New Patients">
            <NRadioGroup v-model:value="formData.accepts_new_patients" class="radio-group">
              <NRadio :value="true">Yes</NRadio>
              <NRadio :value="false">No</NRadio>
            </NRadioGroup>
          </NFormItem>

          <NFormItem label="Telehealth Available">
            <NRadioGroup v-model:value="formData.telehealth_available" class="radio-group">
              <NRadio :value="true">Yes</NRadio>
              <NRadio :value="false">No</NRadio>
            </NRadioGroup>
          </NFormItem>

          <!-- Office Hours with external help text -->
          <div class="form-field-with-help">
            <div class="help-text-external">Enter time in HH:MM format (24-hour). For example: 09:00, 17:30</div>
            <NFormItem label="Office Hours">
              <NDynamicInput v-model:value="formData.office_hours" :on-create="createOfficeHours" #="{ value, index }">
                <div class="office-hours-item">
                  <NSelect v-model:value="formData.office_hours[index].day" :options="[
                    { label: 'Monday', value: 'Monday' },
                    { label: 'Tuesday', value: 'Tuesday' },
                    { label: 'Wednesday', value: 'Wednesday' },
                    { label: 'Thursday', value: 'Thursday' },
                    { label: 'Friday', value: 'Friday' },
                    { label: 'Saturday', value: 'Saturday' },
                    { label: 'Sunday', value: 'Sunday' },
                  ]" class="day-select full-width-select" />
                  <div class="hours-inputs">
                    <NInput v-model:value="formData.office_hours[index].open" placeholder="09:00" class="time-input"
                      :status="getValidationError(`office_hours_${index}_open`) ? 'error' : undefined" />
                    <span class="to-text">to</span>
                    <NInput v-model:value="formData.office_hours[index].close" placeholder="17:00" class="time-input"
                      :status="getValidationError(`office_hours_${index}_close`) ? 'error' : undefined" />
                  </div>
                  <div v-if="getValidationError(`office_hours_${index}_open`)" class="error-text">
                    {{ getValidationError(`office_hours_${index}_open`) }}
                  </div>
                  <div v-if="getValidationError(`office_hours_${index}_close`)" class="error-text">
                    {{ getValidationError(`office_hours_${index}_close`) }}
                  </div>
                  <div v-if="getValidationError(`office_hours_${index}_time`)" class="error-text">
                    {{ getValidationError(`office_hours_${index}_time`) }}
                  </div>
                </div>
              </NDynamicInput>
            </NFormItem>
          </div>

          <NFormItem label="Average Wait Time (minutes)"
            :validation-status="getValidationError('average_wait_time_minutes') ? 'error' : undefined"
            :feedback="getValidationError('average_wait_time_minutes')">
            <NInputNumber v-model:value="formData.average_wait_time_minutes" placeholder="Average wait time in minutes"
              size="large" :show-button="false" :min="0" :max="480"
              :status="getValidationError('average_wait_time_minutes') ? 'error' : undefined" />
          </NFormItem>

          <NFormItem label="Parking Information"
            :validation-status="getValidationError('parking_info') ? 'error' : undefined"
            :feedback="getValidationError('parking_info')">
            <NInput v-model:value="formData.parking_info" type="textarea" placeholder="Parking availability and details"
              :rows="2" size="large" :status="getValidationError('parking_info') ? 'error' : undefined" />
          </NFormItem>

          <NFormItem label="Payment Methods">
            <NSelect v-model:value="formData.payment_methods" multiple :options="paymentMethodOptions"
              placeholder="Select payment methods" filterable tag size="large" />
          </NFormItem>

          <NFormItem label="Accessibility Features">
            <NSelect v-model:value="formData.accessibility_options" multiple :options="accessibilityOpts"
              placeholder="Select accessibility features" filterable tag size="large" />
          </NFormItem>
        </NForm>

        <template #action>
          <NSpace justify="space-between" class="form-actions">
            <NButton @click="prevStep" class="action-button">Back: Professional Details</NButton>
            <NButton type="primary" @click="nextStep" class="action-button">Next: About Me</NButton>
          </NSpace>
        </template>
      </NCard>

      <NCard v-if="currentStep === 6" title="About Me" class="form-card">
        <!-- Tone Selection -->
        <NFormItem label="Writing Tone">
          <NRadioGroup v-model:value="aiOptions.tone" size="large">
            <NRadio value="professional">
              <NSpace vertical :size="0">
                <span>Professional</span>
                <NText depth="3" style="font-size: 12px">Authoritative, credential-focused</NText>
              </NSpace>
            </NRadio>
            <NRadio value="friendly">
              <NSpace vertical :size="0">
                <span>Friendly</span>
                <NText depth="3" style="font-size: 12px">Warm, approachable, patient-centered</NText>
              </NSpace>
            </NRadio>
            <NRadio value="formal">
              <NSpace vertical :size="0">
                <span>Formal</span>
                <NText depth="3" style="font-size: 12px">Traditional, emphasizes expertise</NText>
              </NSpace>
            </NRadio>
          </NRadioGroup>
        </NFormItem>

        <!-- Length Selection -->
        <NFormItem label="Length">
          <NRadioGroup v-model:value="aiOptions.length" size="large">
            <NRadio value="short">Short (2-3 sentences)</NRadio>
            <NRadio value="medium">Medium (1-2 paragraphs)</NRadio>
            <NRadio value="detailed">Detailed (2-3 paragraphs)</NRadio>
          </NRadioGroup>
        </NFormItem>

        <!-- Generate Button -->
        <NSpace justify="center" style="margin-bottom: 16px">
          <NButton type="primary" size="large" @click="handleGenerateAboutMe" :loading="isGenerating"
            :disabled="!canGenerate">
            {{ isGenerating ? 'Generating...' : '✨ Generate with AI' }}
          </NButton>
        </NSpace>

        <!-- About Me Text - ALWAYS VISIBLE -->
        <NFormItem label="About Me Text"
          :validation-status="getValidationError('additional_info') ? 'error' : undefined"
          :feedback="getValidationError('additional_info')">
          <NInput v-model:value="formData.additional_info" type="textarea"
            placeholder="Tell patients about yourself, your experience, and your approach to care... Or click 'Generate with AI' above to create this automatically."
            :rows="8" size="large" :status="getValidationError('additional_info') ? 'error' : undefined" />
        </NFormItem>

        <template #action>
          <NSpace justify="space-between" class="form-actions">
            <NButton @click="prevStep" class="action-button">Back: Practice Information</NButton>
            <NButton type="primary" @click="nextStep" class="action-button">Next: Review & Submit</NButton>
          </NSpace>
        </template>
      </NCard>

      <!-- Step 7: Preview & Submit -->
      <NCard v-if="currentStep === 7" title="Review & Submit" class="form-card">
        <div class="preview-section">
          <div class="preview-header">
            <h2>{{ formData.provider_name || "Provider Name" }}</h2>
            <div v-if="formData.provider_type" class="provider-type">
              {{ formData.provider_type }}
            </div>
            <div v-if="formData.is_draft" class="draft-badge">Draft</div>
          </div>

          <div class="preview-content">
            <div class="preview-column">
              <div v-if="formData.specializations.length > 0" class="preview-item">
                <h4>🏥 Specializations</h4>
                <div class="tags-container">
                  <NTag v-for="spec in formData.specializations" :key="spec" type="primary" class="preview-tag">
                    {{ spec }}
                  </NTag>
                </div>
              </div>

              <div v-if="formData.procedures.length > 0" class="preview-item">
                <h4>⚕️ Procedures</h4>
                <div class="tags-container">
                  <NTag v-for="proc in formData.procedures" :key="proc" type="success" class="preview-tag">
                    {{ proc }}
                  </NTag>
                </div>
              </div>

              <div v-if="formData.insurance_accepted.length > 0" class="preview-item">
                <h4>💳 Insurance Accepted</h4>
                <div class="tags-container">
                  <NTag v-for="ins in formData.insurance_accepted" :key="ins" type="warning" class="preview-tag">
                    {{ ins }}
                  </NTag>
                </div>
              </div>

              <div v-if="formData.spoken_languages.length > 0" class="preview-item">
                <h4>🗣️ Spoken Languages</h4>
                <div class="tags-container">
                  <NTag v-for="lang in formData.spoken_languages" :key="lang" type="info" class="preview-tag">
                    {{ lang }}
                  </NTag>
                </div>
              </div>

              <div v-if="formData.education.length > 0" class="preview-item">
                <h4>🎓 Education</h4>
                <div v-for="(edu, i) in formData.education" :key="i" class="preview-detail-item">
                  <div class="detail-title">{{ edu.degree }}</div>
                  <div class="detail-subtitle">{{ edu.institution }} ({{ edu.year }})</div>
                </div>
              </div>

              <div v-if="formData.certifications.length > 0" class="preview-item">
                <h4>🏆 Certifications</h4>
                <div v-for="(cert, i) in formData.certifications" :key="i" class="preview-detail-item">
                  <div class="detail-title">{{ cert.name }}</div>
                  <div class="detail-subtitle">{{ cert.issuer }} ({{ cert.year }})</div>
                </div>
              </div>

              <div v-if="formData.years_of_experience" class="preview-item">
                <h4>⏱️ Experience</h4>
                <div class="preview-text">{{ formData.years_of_experience }} years</div>
              </div>

              <div v-if="formData.tags.length > 0" class="preview-item">
                <h4>🏷️ Tags</h4>
                <div class="tags-container">
                  <NTag v-for="tag in formData.tags" :key="tag" type="default" class="preview-tag">
                    {{ tag }}
                  </NTag>
                </div>
              </div>

              <div v-if="formData.additional_info" class="preview-item">
                <h4>ℹ️ About</h4>
                <p class="preview-text">{{ formData.additional_info }}</p>
              </div>
            </div>

            <div class="preview-column">
              <div class="preview-item">
                <h4>📍 Primary Address</h4>
                <div class="preview-address" v-if="formData.address_line1 || formData.city">
                  <div v-if="formData.address_line1">
                    {{ formData.address_line1 }}
                  </div>
                  <div v-if="formData.address_line2">
                    {{ formData.address_line2 }}
                  </div>
                  <div v-if="formData.address_line3">
                    {{ formData.address_line3 }}
                  </div>
                  <div v-if="formData.city || formData.state || formData.postal_code">
                    {{ formData.city }}{{ formData.city && (formData.state || formData.postal_code) ? ", " : "" }}
                    {{ formData.state }} {{ formData.postal_code }}
                  </div>
                </div>
                <div v-else class="preview-empty">No address provided</div>
              </div>

              <div v-if="formData.additional_locations.length > 0" class="preview-item">
                <h4>📍 Additional Locations</h4>
                <div v-for="(loc, i) in formData.additional_locations" :key="i" class="preview-detail-item">
                  <div>{{ loc.address }}</div>
                  <div>{{ loc.city }}, {{ loc.state }} {{ loc.postal_code }}</div>
                </div>
              </div>

              <div v-if="formData.phone_numbers.length > 0" class="preview-item">
                <h4>📞 Phone Numbers</h4>
                <div v-for="phone in formData.phone_numbers" :key="phone" class="contact-item">
                  {{ phone }}
                </div>
              </div>

              <div v-if="formData.website_url" class="preview-item">
                <h4>🌐 Website</h4>
                <div class="contact-item">{{ formData.website_url }}</div>
              </div>

              <div v-if="formData.social_media.length > 0" class="preview-item">
                <h4>📱 Social Media</h4>
                <div v-for="(social, i) in formData.social_media" :key="i" class="contact-item">
                  <strong>{{ social.platform }}:</strong> {{ social.url }}
                </div>
              </div>

              <div v-if="formData.office_hours.length > 0" class="preview-item">
                <h4>🕒 Office Hours</h4>
                <div v-for="(hours, i) in formData.office_hours" :key="i" class="contact-item">
                  <strong>{{ hours.day }}:</strong> {{ hours.open }} - {{ hours.close }}
                </div>
              </div>

              <div v-if="formData.average_wait_time_minutes !== null" class="preview-item">
                <h4>⏱️ Average Wait Time</h4>
                <div class="contact-item">{{ formData.average_wait_time_minutes }} minutes</div>
              </div>

              <div v-if="formData.parking_info" class="preview-item">
                <h4>🅿️ Parking Information</h4>
                <div class="contact-item">{{ formData.parking_info }}</div>
              </div>

              <div v-if="formData.payment_methods.length > 0" class="preview-item">
                <h4>💰 Payment Methods</h4>
                <div class="tags-container">
                  <NTag v-for="method in formData.payment_methods" :key="method" type="info" class="preview-tag">
                    {{ method }}
                  </NTag>
                </div>
              </div>

              <div v-if="formData.accessibility_options.length > 0" class="preview-item">
                <h4>♿ Accessibility</h4>
                <div class="tags-container">
                  <NTag v-for="option in formData.accessibility_options" :key="option" type="success"
                    class="preview-tag">
                    {{ option }}
                  </NTag>
                </div>
              </div>

              <div class="preview-item">
                <h4>🧑‍⚕️ Practice Details</h4>
                <div class="practice-details">
                  <div v-if="formData.accepts_new_patients !== null">
                    <strong>Accepting New Patients:</strong>
                    {{ formData.accepts_new_patients ? "Yes" : "No" }}
                  </div>
                  <div v-if="formData.telehealth_available !== null">
                    <strong>Telehealth Available:</strong>
                    {{ formData.telehealth_available ? "Yes" : "No" }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <template #action>
          <NSpace justify="space-between" class="form-actions">
            <NButton @click="prevStep" class="action-button">Back: About Me</NButton>
            <NButton type="primary" @click="saveProvider" :loading="saving" class="action-button">
              {{ isEditMode ? "Update Provider" : "Create Provider" }}
            </NButton>
          </NSpace>
        </template>
      </NCard>
    </div>
  </div>
</template>

<style scoped>
.provider-form-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.form-card {
  margin-bottom: 2rem;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

/* Step indicator styles */
.step-indicator {
  margin-bottom: 2rem;
}

.step-progress {
  height: 6px;
  background-color: #eaeaea;
  border-radius: 3px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: #18a058;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.step-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.step-title h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #333;
}

.step-count {
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

.form-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1rem;
}

.form-col {
  margin-bottom: 0;
}

.address-preview {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #18a058;
}

.address-preview h4 {
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 0.9rem;
}

.preview-address {
  color: #555;
  line-height: 1.5;
}

/* External help text styling */
.form-field-with-help {
  margin-bottom: 1.5rem;
}

.help-text-external {
  font-size: 0.8rem;
  color: #10b981;
  margin-bottom: 0.5rem;
  line-height: 1.4;
  font-style: italic;
  background-color: #f0fdf4;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border-left: 3px solid #10b981;
}

.preview-section {
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e0e0e6;
}

.preview-header {
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e0e0e6;
  position: relative;
}

.preview-header h2 {
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1.8rem;
}

.provider-type {
  font-size: 1rem;
  color: #666;
}

.draft-badge {
  position: absolute;
  top: 0;
  right: 0;
  background-color: #ff9800;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: bold;
}

.preview-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.preview-column {
  display: flex;
  flex-direction: column;
}

.preview-item {
  margin-bottom: 1.5rem;
}

.preview-item:last-child {
  margin-bottom: 0;
}

.preview-item h4 {
  margin: 0 0 0.75rem 0;
  color: #333;
  font-size: 1rem;
  font-weight: 600;
}

.preview-text {
  margin: 0;
  color: #555;
  line-height: 1.6;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.preview-tag {
  margin: 0;
}

.contact-item {
  color: #555;
  margin-bottom: 0.5rem;
  padding: 0.25rem 0;
}

.contact-item:last-child {
  margin-bottom: 0;
}

.preview-empty {
  color: #999;
  font-style: italic;
}

.preview-detail-item {
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #eee;
}

.preview-detail-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.detail-title {
  font-weight: 600;
  color: #333;
}

.detail-subtitle {
  color: #666;
  font-size: 0.9rem;
}

/* Dynamic input styling */
.social-media-item,
.education-item,
.certification-item,
.office-hours-item {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  margin-bottom: 0.5rem;
}

.platform-select,
.day-select {
  width: 100%;
}

.url-input {
  width: 100%;
  margin-top: 0.5rem;
}

.hours-inputs {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.time-input {
  flex: 1;
  min-width: 80px;
}

.to-text {
  color: #666;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.year-input {
  width: 120px;
  flex-shrink: 0;
}

.flex-input {
  flex: 1;
}

.full-width-input,
.full-width-select {
  width: 100%;
}

.compact-hint {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.75rem;
  line-height: 1.4;
}

.error-text {
  font-size: 0.8rem;
  color: #d03050;
  margin-top: 0.25rem;
  word-wrap: break-word;
}

.additional-location {
  padding: 1rem;
  background: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 0.5rem;
}

.practice-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.radio-group {
  display: flex;
  gap: 1rem;
}

.mb-2 {
  margin-bottom: 8px;
}

.w-full {
  width: 100%;
}

.form-actions {
  margin-top: 1rem;
}

.action-button {
  min-width: 120px;
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  .provider-form-container {
    padding: 1rem 0.5rem;
  }

  .step-title h3 {
    font-size: 1rem;
  }

  .step-count {
    font-size: 0.8rem;
  }

  .form-row,
  .mobile-form-row {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .preview-content {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .social-media-item {
    gap: 0.5rem;
  }

  .url-input {
    margin-top: 0.5rem;
  }

  .hours-inputs {
    margin-top: 0.5rem;
  }

  .time-input {
    min-width: 70px;
  }

  .to-text {
    font-size: 0.8rem;
  }

  .compact-hint {
    font-size: 0.75rem;
  }

  .help-text-external {
    font-size: 0.75rem;
    padding: 0.4rem 0.6rem;
  }

  .error-text {
    font-size: 0.75rem;
    line-height: 1.3;
  }

  .form-actions {
    flex-direction: column;
    gap: 0.75rem;
  }

  .action-button {
    width: 100%;
    min-width: auto;
  }

  .radio-group {
    display: flex;
    gap: 1rem;
  }

  .preview-section {
    padding: 1rem;
  }

  .preview-header h2 {
    font-size: 1.4rem;
  }

  .draft-badge {
    position: static;
    display: inline-block;
    margin-top: 0.5rem;
  }

  /* NDynamicInput mobile fixes */
  :deep(.n-dynamic-input__item) {
    width: 100% !important;
    margin-bottom: 1rem;
  }

  :deep(.n-dynamic-input__item-content) {
    width: 100% !important;
    flex: 1;
  }

  :deep(.n-dynamic-input__item-action) {
    flex-shrink: 0;
    margin-left: 0.5rem;
  }

  /* Input component mobile fixes */
  :deep(.n-input),
  :deep(.n-select),
  :deep(.n-input-number) {
    width: 100%;
  }

  /* Card responsive fixes */
  :deep(.n-card__content) {
    padding: 1rem;
  }

  :deep(.n-card__action) {
    padding: 1rem;
  }

  /* Form item mobile fixes */
  :deep(.n-form-item__feedback) {
    word-break: break-word;
    white-space: normal;
  }
}

/* Small mobile screens */
@media (max-width: 480px) {
  .provider-form-container {
    padding: 0.5rem 0.25rem;
  }

  .time-input {
    min-width: 60px;
  }

  .to-text {
    font-size: 0.75rem;
  }

  .step-title h3 {
    font-size: 0.9rem;
  }

  .step-count {
    font-size: 0.75rem;
  }

  .preview-header h2 {
    font-size: 1.2rem;
  }

  .compact-hint {
    font-size: 0.7rem;
  }

  .help-text-external {
    font-size: 0.7rem;
  }

  .error-text {
    font-size: 0.7rem;
  }

  .year-input {
    width: 100px;
  }
}
</style>